import { useDispatch, useSelector } from 'react-redux';
import Banner from '@cpg-its/ads.containers.banner';
import { useHistory } from 'react-router';
import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikProps } from 'formik';
import React from 'react';
import { FormField } from '../../../../../../global/components/form-element/form-field';
import { NumberField, NumberInputTypes } from '../../../../../../global/components/form-element/number-field';
import { DividerSection } from '../../../../../../global/components/layout/divider-section';
import { FormGroup } from '../../../../../../global/components/layout/form-group';
import { BenefitEnrollmentStep, GetNewHirePensionEnrollmentValidationSchema, PensionEnrollmentFormValues, ExcludedPensionID } from '../../models/pension-enrollment-models';
import { TcsmStates } from '../../../../../../root/tcsm-model';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { FormWrapper } from '../../../../../../global/components/form-wrapper';
import { ContentSpacer } from '../../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../../global/components/wysiwyg-with-react-link';
import { BillPayerOption, PensionEnrollmentContributionType, PensionEnrollmentState } from '../../../pension-enrollment/models/pension-enrollment-model';
import { getNumberPercent } from '../../../../../../global/helpers/transformer';
import { InstitutionState } from '../../../institutions/institution-model';
import { getLetterMonthDate } from '../../../../../../global/helpers/date-helpers';
import { EnrollPension, GetInitialPensionFormValue } from '../helpers/pension-enrollment-helpers';
import { Employee, ScheduledHours } from '../../../employment/employment-model';
import { useApiResponseError } from '../../../../../../global/hooks/useApiError';
import { FormTypes } from '../../../../../account-info/models/form-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../slice/new-hire-enrollment-slices';
import { NewHireEnrollmentRoutes } from '../../models/new-hire-routes';
import { AccountInfoRoutes } from '../../../../../account-info/constants/routes';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { ConfirmationStatusCode } from '../../models/new-hire-confirmation-models';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../../constants/routes';
import { isInstitutionOfficerOnly } from '../../../../../../global/helpers/auth-helpers';
import { memberScheduleHoursHighThreshold } from '../../models/new-hire-enrollment-models';

export const billingAccountOptions = [
	{
		label: 'Institution',
		value: BillPayerOption.Employer,

	},
	{
		label: 'Diocese',
		value: BillPayerOption.Diocese,

	}];


const contributionType = [
	{
		label: 'By Percentage',
		value: PensionEnrollmentContributionType.Percentage,

	},
	{
		label: 'By Fixed Amount',
		value: PensionEnrollmentContributionType.FixedAmount,

	}];

interface Props {
	basePath: string;
}

export function PensionEnrollmentForm(props: Props) {

	const { basePath } = props;

	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state.epiProps?.newHireSettingsState?.epiContent);
	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);
	const pensionEnrollmentState = useSelector<TcsmStates, PensionEnrollmentState>(state => state.pensionEnrollmentState);
	const plansAvailable = pensionEnrollmentState?.pensionInfo?.plans?.filter(plan => (plan.eligibility_result?.errors?.length || 0) < 1);
	const enrolledPlans = pensionEnrollmentState?.pensionInfo?.enrolled_plans;
	const insitution = useSelector<TcsmStates, InstitutionState>(state => state?.institutionState);
	const employee = useSelector<TcsmStates, Employee>(state => state?.employmentState?.currentEmployee);
	const initialValues: PensionEnrollmentFormValues = {
		selectedPlans: enrolledPlans && enrolledPlans?.length > 0 ? enrolledPlans?.map(plan => `${plan?.plan_id}_${plan?.plan_type}`) : plansAvailable?.map(plan => `${plan?.plan_id}_${plan?.plan_type}`),
	};
	const dispatch = useDispatch();
	const apiError = useApiResponseError(FormTypes.NEWHIRE_PENSION_ENROLLMENT);
	const history = useHistory();
	const institutionOfficerOnly = isInstitutionOfficerOnly();
	const scheduledHour = useSelector<TcsmStates, ScheduledHours>(state => state.employmentState?.currentEmployment?.scheduled_hours[0]);
	const below1000 = Number.isInteger(scheduledHour?.hours) && scheduledHour?.hours < memberScheduleHoursHighThreshold;
	const confirmationLink = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.NewHireEnrollment}${NewHireEnrollmentRoutes.Confirmation}?status=${institutionOfficerOnly ? ConfirmationStatusCode.PENDING : ConfirmationStatusCode.NH_BY_DIO}`);

	const validationSchema = GetNewHirePensionEnrollmentValidationSchema(newHireProps, labelSetting, plansAvailable);
	function onSubmit(values) {
		if (!plansAvailable || plansAvailable?.length === 0) {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.COVERAGE));
			history.push(`${basePath}${NewHireEnrollmentRoutes.Coverage}`);
			return;

		}

		const onSuccess = () => {
			dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.COVERAGE));
			history.push(below1000 ? confirmationLink : `${basePath}${NewHireEnrollmentRoutes.Coverage}`);
		};

		if (values?.selectedPlans?.length === 0) {
			onSuccess();
			return;
		}

		EnrollPension(values, plansAvailable, employee?.guid, employee?.employment_id, dispatch, onSuccess);
	}


	const onPlanChange = (formikProps, e, fieldArrayProps: FieldArrayRenderProps) => {
		const values = formikProps.values.selectedPlans;
		const { value } = e.target;
		if (e.target.checked) {
			fieldArrayProps.push(value);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
		}
	};

	function cancelAction() {
		dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.RELATIONSHIP));
		history.push(`${basePath}${AccountInfoRoutes.RelationshipsSegment}`);

	}


	function NoAvailablePlans() {
		if (!plansAvailable || plansAvailable?.length === 0)
			return (
				<ContentSpacer>
					<WysiwygWithReactLink content={newHireProps?.noAvailablePlanMessage} />
				</ContentSpacer>
			);
		return null;
	}

	function handleSubmitButtonText() {

		if (!plansAvailable || plansAvailable?.length === 0) {
			return newHireProps.continueButtonTextWhenNoRetirementPlansAreOffered;
		}

		return newHireProps?.retirementAdminContinueButtonText;
	}


	function RenderFormContent(formikProps: FormikProps<PensionEnrollmentFormValues>) {
		if (!(plansAvailable?.length > 0) && !(enrolledPlans?.length > 0)) {
			return (
				<ContentSpacer>
					<WysiwygWithReactLink content={newHireProps.noRetirementPlansOfferedMessage} />
				</ContentSpacer>
			);
		}

		return (
			<DividerSection removeDivider sectionText={newHireProps?.planSelectionTitle} >
				<ContentSpacer>
					<h3>{newHireProps?.eligiblePlanTitle}</h3>
					<WysiwygWithReactLink content={newHireProps?.eligiblePlanDescription} />
				</ContentSpacer>
				<FieldArray
					name='selectedPlans'
					render={(fieldArrayProps) => (
						<>
							<NoAvailablePlans />
							{
								enrolledPlans?.map(enrolledPlan => {
									return (
										<ContentSpacer key={`enrolledplan-${enrolledPlan?.id}`}>
											<ContentSpacer>
												<h3>{enrolledPlan?.product?.description}</h3>
											</ContentSpacer>
											{
												enrolledPlan?.pbpor_id !== ExcludedPensionID.LAY_DB_PENSION_PLAN_ID && enrolledPlan?.pbpor_id !== ExcludedPensionID.CLERGY_DB_PENSION_PLAN_ID && (
													<>
														<ContentSpacer>
															<p><strong>{newHireProps?.preTaxContributionLabel}</strong></p>
															<p>{enrolledPlan?.employee_contribution}</p>
														</ContentSpacer>
													</>
												)
											}
											<ContentSpacer>
												<p><strong>{newHireProps?.effectiveDateLabel}</strong></p>
												<p>{getLetterMonthDate(enrolledPlan?.enrollment_effective_date)}</p>
											</ContentSpacer>
										</ContentSpacer>
									);
								})
							}
							{
								plansAvailable?.map((plan, idx) => {
									return (
										<DividerSection removeDivider key={`plan-${plan?.id}`} >
											{
												apiError && apiError[idx] &&
												<ContentSpacer>
													<Banner
														banner={{
															type: 'error',
															label: <p><strong>{apiError[idx]}</strong></p>,
														}}
													/>
												</ContentSpacer>
											}
											<FormGroup>
												<FormField
													id={plan.plan_id}
													name='selectedPlans'
													label={plan?.product?.description}
													component='checkBox'
													touched={formikProps.touched}
													errors={formikProps.errors}
													value={`${plan?.plan_id}_${plan?.plan_type}`}
													checked={formikProps.values.selectedPlans?.includes(`${plan?.plan_id}_${plan?.plan_type}`)}
													checkBoxGroup
													onChange={(e) => onPlanChange(formikProps, e, fieldArrayProps)}

												/>
												<FormGroup indent>
													<FormField
														name={`billingAccountType${plan.plan_id}`}
														label={newHireProps?.billingAccountLabel}
														component='radioGroup'
														options={billingAccountOptions}
														touched={formikProps.touched}
														errors={formikProps.errors}
														required
													/>
													{
														!!plan?.employer_match && (
															<>
																<div className='form-item'>
																	<WysiwygWithReactLink content={newHireProps?.organizationNameContributionPayPeriod?.replace('{orgName}', insitution?.currentInstitution?.organization_name)} />
																	<p>{getNumberPercent(plan?.employer_match)}</p>
																</div>
															</>
														)
													}
													{
														plan?.pbpor_id !== ExcludedPensionID.LAY_DB_PENSION_PLAN_ID && plan?.pbpor_id !== ExcludedPensionID.CLERGY_DB_PENSION_PLAN_ID && (
															<>
																<FormField
																	name={`type${plan.plan_id}`}
																	label={newHireProps?.employeeContributionLabel}
																	component='radioGroup'
																	options={contributionType}
																	touched={formikProps.touched}
																	errors={formikProps.errors}
																	subLabel={newHireProps?.employeeContributionSubLabel}
																	tooltip={{
																		title: newHireProps?.employeeContributionLabelHelperText,
																	}}
																	required
																/>
																<NumberField
																	name={`amount${plan.plan_id}`}
																	label={newHireProps?.preTaxContributionLabel}
																	autoWidth
																	type={
																		formikProps?.values[`type${plan.plan_id}`] === PensionEnrollmentContributionType.Percentage ?
																			NumberInputTypes.Percentage :
																			NumberInputTypes.Currency
																	}
																	formikProps={formikProps}
																	required
																/>
															</>
														)
													}
													<div className='form-item'>
														<p><strong>{newHireProps?.effectiveDateLabel}</strong></p>
														<p>{getLetterMonthDate(plan?.enrollment_date)}</p>
													</div>
												</FormGroup>
											</FormGroup>
										</DividerSection>
									);
								})
							}

						</>
					)}
				/>
				<ContentSpacer>
					<WysiwygWithReactLink content={newHireProps?.legalNotificationTexts} />
				</ContentSpacer>
			</DividerSection>);
	}
	return (

		<Formik
			initialValues={GetInitialPensionFormValue(initialValues, plansAvailable)}
			onSubmit={(values) => onSubmit(values)}
			enableReinitialize
			validationSchema={validationSchema}
		>
			{
				(formikProps: FormikProps<PensionEnrollmentFormValues>) => {
					return (

						<Form>
							<FormWrapper
								formikProps={formikProps}
								submitText={handleSubmitButtonText()}
								cancelText={newHireProps?.retirementAdminBackButtonText}
								onCancel={cancelAction}
								preventButtonDisableValidation
							>
								{
									RenderFormContent(formikProps)
								}
							</FormWrapper>
						</Form>
					);
				}
			}
		</Formik>

	);
}